/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'

// Chakra Components
import {
    Box,
    Flex,

    // Hooks
    useMultiStyleConfig,
    Stack,
    HStack,
    omitThemingProps,
    IconButton,
    List,
    Img,
    ListItem
} from '@chakra-ui/react'
import {EnterKeyNumber} from '../../constants'
import lazyLoadable from '../../utils/lazy-loadable'
import {getFileNameFromUrl} from '../../utils/url'
import DynamicImage from '../dynamic-image'
import {HotspotIcon, LineIcon} from '../icons'
import LoadingSpinner from '../loading-spinner'
const ReactPlayerLoader = lazyLoadable(() => import('@brightcove/react-player-loader'), {
    ssr: false,
    fallback: <LoadingSpinner />
})

/**
 * The image gallery displays a hero image and thumbnails below it. You can control which
 * image groups that are use by passing in the current selected variation values.
 */
const ImageGallery = (props) => {
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('ImageGallery', props)
    const {
        children,
        cursor,
        items = [],
        index: initialSelectedIndex = 0,
        imageProps,
        hotspots,
        navLeftIcon,
        navRightIcon,
        preload,
        showChildrenIn,
        showPagination,
        showThumbnails,
        onClick,
        enableZoom,
        onHotspotClick,
        onChange
    } = omitThemingProps(props)
    const brightcovePlayersRefs = useRef([])

    /* Touch event slide/swipe starting point used to calculate left or right direction */
    const touchStart = useRef(0)

    /* Current selected image index */
    const [selectedIndex, _setSelectedIndex] = useState(initialSelectedIndex)
    const setSelectedIndex = (index, notifyChange) => {
        _setSelectedIndex(index)
        if (notifyChange) {
            onChange?.(index)
        }
    }

    /* Indicator that sets to true when slider starts scrolling to prevent user clicks while slider still scrolling to next/prev image */
    const [stillScrolling, setStillScrolling] = useState(false)

    const location = useLocation()
    const scrollRef = useRef()

    useEffect(() => {
        // 1. always make sure we are selecting an index that exists
        // 2. reset position when items change - that is an indirect indication of pdp change
        const index = initialSelectedIndex <= items.length ? initialSelectedIndex : 0
        // reset the selected index when location search changes or initially selected index changes
        _setSelectedIndex(index)
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollRef.current.clientWidth * index
        }
    }, [location.search, initialSelectedIndex, items])

    useEffect(() => {
        for (const playerRef of brightcovePlayersRefs.current) {
            playerRef?.player?.pause?.()
        }
    }, [selectedIndex])

    /* Copied from ImageGallery component with an addition of touch attribute */
    // Scroll the container left or right by 1 image. Passing no args or `1`
    // scrolls to the right, and passing `-1` scrolls left.
    const scroll = (direction = 1, touch) => {
        if (scrollRef.current) {
            /* If scrolling to next/prev image is not finished yet - return */
            if (stillScrolling) return

            /* Calculate slide direction (left/right) from touchevent in mobileview */
            const {touchStart, touchEnd} = touch || {}

            if (touchStart) {
                direction = touchEnd < touchStart ? 1 : -1
            }

            /* In mobileview prevent the user from scrolling just by clicking the image */
            if ((touchStart && Math.abs(touchStart - touchEnd) > 50) || !touchStart) {
                let nextScrollPosition = 0

                if (direction < 0 && selectedIndex === 0) {
                    /* Clicking prev when the slider is on first image scrolls all the way to last image */
                    nextScrollPosition = scrollRef.current.scrollWidth
                    setSelectedIndex(items.length - 1, true)
                } else if (direction > 0 && selectedIndex + 1 === items.length) {
                    /* Clicking next when the slider is on last image scrolls all the way to first image */
                    nextScrollPosition = -scrollRef.current.scrollWidth
                    setSelectedIndex(0, true)
                } else {
                    /* Scroll by one image left/right */
                    nextScrollPosition = direction * scrollRef.current.clientWidth
                    setSelectedIndex(selectedIndex + direction, true)
                }

                scrollRef.current.scrollBy({
                    top: 0,
                    left: nextScrollPosition,
                    behavior: 'smooth'
                })

                /* Prevent the user from clicking slider arrows while scrolling animation is not done yet */
                setStillScrolling(true)
                setTimeout(() => {
                    setStillScrolling(false)
                }, 500)
            }
        }
    }

    /* When it is first time playing the video - always open on fullscreen */
    const onSuccess = ({ref}) => {
        const {player_, posterImage, bigPlayButton} = ref

        posterImage.el_.addEventListener('click', () => {
            player_.requestFullscreen()
        })

        bigPlayButton.el_.addEventListener('click', () => {
            player_.requestFullscreen()
        })

        player_.el().style.width = '100%'
        player_.el().style.height = '100%'
        player_.el().parentElement.style.width = '100%'
        player_.el().parentElement.style.height = '100%'
    }

    return (
        <>
            {items?.length > 0 && (
                <Box {...styles.container}>
                    <Box position="relative" {...styles.imageAndOverlayContainer}>
                        <Box position="relative" {...styles.imageContainer}>
                            <Stack
                                data-testid="image-gallery-scroller"
                                ref={scrollRef}
                                direction="row"
                                spacing={0}
                                wrap="nowrap"
                                overflowX="scroll"
                                sx={{
                                    scrollSnapType: 'x mandatory',
                                    WebkitOverflowScrolling: 'touch' // Safari touch scrolling needed for scroll snap
                                }}
                                css={{
                                    ...styles.scrollBar
                                }}
                                onTouchStart={(e) => {
                                    touchStart.current = e.changedTouches[0].clientX
                                }}
                                onTouchEnd={(e) => {
                                    scroll(1, {
                                        touchStart: touchStart.current,
                                        touchEnd: e.changedTouches[0].clientX
                                    })
                                }}
                            >
                                {/* all images available for swipe */}
                                {items.map((item, idx) => (
                                    <Box key={item?.id || idx} {...styles.imageAspectParent}>
                                        <Flex {...styles.imageAspectChild}>
                                            {/* image background color */}
                                            <Box
                                                {...styles.imageBackgroundOverlay}
                                                data-testid="image-background-overlay"
                                                cursor={cursor}
                                                onClick={(event) => onClick?.(event, item, idx)}
                                                role="button"
                                                aria-label={formatMessage({
                                                    id: 'image_gallery.button.assistive_msg.zoom',
                                                    defaultMessage: 'Open Zoom Gallery'
                                                })}
                                            />

                                            {item?.accountId ? (
                                                <ReactPlayerLoader
                                                    {...item}
                                                    options={{
                                                        autoplay: false
                                                    }}
                                                    embedOptions={{
                                                        responsive: true
                                                    }}
                                                    onSuccess={onSuccess}
                                                    ref={(playerRef) => {
                                                        brightcovePlayersRefs.current[idx] =
                                                            playerRef
                                                    }}
                                                />
                                            ) : (
                                                <>
                                                    <DynamicImage
                                                        data-testid="emotional-gallery-image"
                                                        {...styles.image}
                                                        {...imageProps}
                                                        src={`${
                                                            item.disBaseLink || item.link
                                                        }[?sw={width}&q=60]`}
                                                        imageProps={{
                                                            ...styles.image?.imageProps,
                                                            ...imageProps?.imageProps,
                                                            alt: item.alt,
                                                            loading: idx > 0 ? 'lazy' : 'eager'
                                                        }}
                                                        enableZoom={enableZoom}
                                                        preload={preload && idx === 0}
                                                    />
                                                    {hotspots?.[
                                                        getFileNameFromUrl(
                                                            item.disBaseLink || item.link
                                                        )
                                                    ]?.map((hotspot, index) => (
                                                        <IconButton
                                                            key={index}
                                                            data-testid="hotspot"
                                                            {...styles.hotspot}
                                                            left={`${hotspot.x}%`}
                                                            top={`${hotspot.y}%`}
                                                            variant="hotspot"
                                                            aria-label={formatMessage({
                                                                id: 'image_gallery.button.assistive_msg.hotspot',
                                                                defaultMessage: 'Hotspot'
                                                            })}
                                                            onClick={() => onHotspotClick(hotspot)}
                                                            role="button"
                                                        >
                                                            <HotspotIcon boxSize={10} />
                                                        </IconButton>
                                                    ))}
                                                </>
                                            )}
                                        </Flex>
                                    </Box>
                                ))}

                                {/* controls if we have more than one image/video */}
                                {items.length > 1 && (
                                    <>
                                        {navLeftIcon && (
                                            <Box
                                                {...styles.navButton}
                                                {...styles.navButtonLeft}
                                                onClick={() => scroll(-1)}
                                                role="button"
                                            >
                                                {React.cloneElement(navLeftIcon, {
                                                    'data-testid': 'emotional-gallery-nav-left',
                                                    'aria-label': 'Scroll images left',
                                                    ...styles.sliderButtons
                                                })}
                                            </Box>
                                        )}

                                        {navRightIcon && (
                                            <Box
                                                {...styles.navButton}
                                                {...styles.navButtonRight}
                                                onClick={() => scroll(1)}
                                                role="button"
                                            >
                                                {React.cloneElement(navRightIcon, {
                                                    'data-testid': 'emotional-gallery-nav-right',
                                                    'aria-label': 'Scroll images right',
                                                    ...styles.sliderButtons
                                                })}
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Stack>
                        </Box>

                        <Flex {...styles.overlay}>
                            {showPagination && (
                                <HStack
                                    {...styles.controls}
                                    align="end"
                                    data-testid="emotional-gallery-pagination"
                                >
                                    {items?.map((_, index) => {
                                        return (
                                            <Stack
                                                spacing={1}
                                                key={index}
                                                cursor="pointer"
                                                onClick={() => scroll(index - selectedIndex)}
                                                role="button"
                                                aria-label={formatMessage(
                                                    {
                                                        id: 'image_gallery.button.assistive_msg.go_to_image',
                                                        defaultMessage: 'Go to image {index}'
                                                    },
                                                    {index: index + 1}
                                                )}
                                                /* Maximum amount of 6 lines for pagination (mobile.) */
                                                display={
                                                    index > 5 ? {base: 'none', sm: 'flex'} : 'flex'
                                                }
                                                {...styles.paginationItem}
                                            >
                                                <LineIcon
                                                    height="1px"
                                                    visibility={selectedIndex !== index && 'hidden'}
                                                />
                                                <LineIcon height="1px" />
                                            </Stack>
                                        )
                                    })}
                                </HStack>
                            )}

                            {showChildrenIn === 'overlay' && children}
                        </Flex>
                    </Box>

                    {showThumbnails && (
                        <List {...styles.thumbnailsContainer}>
                            {items.map((item, index) => {
                                const selected = index === selectedIndex
                                return (
                                    <ListItem
                                        {...styles.thumbnailImageItem}
                                        {...(selected && styles.thumbnailImageItemSelected)}
                                        tabIndex={0}
                                        key={index}
                                        data-testid="image-gallery-thumbnails"
                                        onKeyDown={(e) => {
                                            if (e.keyCode === EnterKeyNumber) {
                                                return scroll(index - selectedIndex)
                                            }
                                        }}
                                        onClick={() => scroll(index - selectedIndex)}
                                        role="button"
                                    >
                                        <Img
                                            {...styles.thumbnailImage}
                                            alt={item.alt}
                                            src={item.thumbnail || item.disBaseLink}
                                        />
                                    </ListItem>
                                )
                            })}
                        </List>
                    )}

                    {showChildrenIn === 'container' && children}
                </Box>
            )}
        </>
    )
}

ImageGallery.propTypes = {
    /**
     * Children to render inside overlay
     */
    children: PropTypes.any,
    /**
     * The images/videos array to be rendered
     */
    items: PropTypes.array,
    /**
     * Additional props to pass to DynamicImage like `widths` and `imageProps`
     */
    imageProps: PropTypes.object,
    /**
     * List of hotspots per image name
     */
    hotspots: PropTypes.object,
    /**
     * Icon to use for left image navigation
     */
    navLeftIcon: PropTypes.any,
    /**
     * Icon to use for right image navigation
     */
    navRightIcon: PropTypes.any,
    /**
     * Index of the initially displayed image
     */
    index: PropTypes.number,
    /**
     * Should the first image be preloaded (useful for SSR)
     */
    preload: PropTypes.bool,
    /**
     * Specify where to render children
     */
    showChildrenIn: PropTypes.oneOf(['overlay', 'container']),
    /**
     * Should the pagination control be shown
     */
    showPagination: PropTypes.bool,
    /**
     * Should the thumbnails be shown
     */
    showThumbnails: PropTypes.bool,
    /**
     * The image gallery variant
     */
    variant: PropTypes.string,
    /**
     * Handler for click on image
     */
    onClick: PropTypes.func,
    /**
     * Handler for change of image
     */
    onChange: PropTypes.func,
    /**
     * Handler for click on hotspot
     */
    onHotspotClick: PropTypes.func
}

export default ImageGallery
